import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';
import { Moon, CalendarDays, ArrowLeft, KeyRound, Mail, ShieldCheck, CalendarCheck, Globe, Eye, EyeOff } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { authAPI, setStoredToken } from '../utils/api';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, login, register, setUser } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const [view, setView] = useState('login'); // 'login' | 'signup' | 'forgot' | 'code' | 'reset-success'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [devCode, setDevCode] = useState(''); // kept for compatibility; no longer populated from backend
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const redirectTo = location.state?.from?.pathname || '/home';

  useEffect(() => {
    if (!authLoading && user) navigate(redirectTo, { replace: true });
  }, [user, authLoading, navigate, redirectTo]);

  // Cleanup cooldown timer
  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const startCooldown = (seconds = 60) => {
    setCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (view === 'signup' && !name.trim()) {
      setError(t('auth.name_required'));
      return;
    }
    setLoading(true);
    try {
      if (view === 'login') {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate(redirectTo);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await authAPI.forgotPassword(resetEmail, lang);
      // Note: _dev_code is no longer returned by the backend for security reasons
      setView('code');
      startCooldown(60);
      if (res._email_failed) {
        setSuccess(t('auth.code_sent_fallback'));
      } else {
        setSuccess(t('auth.code_sent'));
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        const match = typeof detail === 'string' && detail.match(/(\d+)/);
        startCooldown(match ? parseInt(match[1]) : 60);
        setError(detail || t('auth.cooldown'));
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to send reset code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwords_no_match'));
      return;
    }
    if (newPassword.length < 6) {
      setError(t('auth.password_min'));
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(resetEmail, resetCode, newPassword);
      setView('reset-success');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    setError('');
    setLoading(true);
    try {
      const userData = await authAPI.googleExchange(tokenResponse.access_token);
      if (userData?.token) setStoredToken(userData.token);
      setUser(userData);
      navigate(redirectTo);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => setError('Google sign-in failed. Please try again.'),
    flow: 'implicit',
  });

  const handleSocialLogin = (provider) => {
    if (provider === 'Google') {
      googleLogin();
    } else {
      setError(`${provider} login coming soon!`);
    }
  };

  const goBackToLogin = () => {
    setView('login');
    setError('');
    setSuccess('');
    setDevCode('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const langShort = { en: 'EN', ar: 'ع', fa: 'فا', tr: 'TR', id: 'ID', ms: 'MS' };

  // While checking stored session — show a blank spinner, not the login form
  if (authLoading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex flex-col items-center justify-center p-6 relative">
      {/* Language dropdown - top right */}
      <div ref={langRef} className="absolute top-4 right-4 z-50" data-testid="auth-lang-selector">
        <button onClick={() => setLangOpen(!langOpen)} data-testid="auth-lang-trigger"
          className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-xl shadow-sm border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
          <Globe size={15} className="text-slate-400" />
          <span>{langShort[lang] || 'EN'}</span>
        </button>
        {langOpen && (
          <div className="absolute right-0 mt-1.5 bg-white rounded-xl shadow-lg border border-slate-200 py-1 min-w-[140px] overflow-hidden">
            {Object.entries(languages).map(([code, name]) => (
              <button key={code} onClick={() => { setLang(code); setLangOpen(false); }} data-testid={`auth-lang-${code}`}
                className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between transition-colors ${
                  lang === code ? 'bg-slate-900 text-white font-semibold' : 'text-slate-700 hover:bg-slate-50'
                }`}>
                <span>{name}</span>
                <span className="text-xs opacity-60">{langShort[code]}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">
        {/* Logo/Branding */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl flex items-center justify-center shadow-lg">
              <CalendarCheck className="text-white" size={32} />
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg">
              <CalendarDays className="text-white" size={32} />
            </div>
          </div>
          <p className="text-base font-medium text-slate-400 mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('auth.welcome_to')}</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>iRemindit</h1>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('app.tagline')}</p>
        </div>

        <AnimatePresence mode="wait">
          {/* ─── Login / Signup ─── */}
          {(view === 'login' || view === 'signup') && (
            <motion.div key="auth" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
              className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] space-y-4">
              <div className="flex bg-slate-100 p-1 rounded-full mb-6">
                <button onClick={() => { setView('login'); setError(''); }} data-testid="auth-login-tab"
                  className={`flex-1 py-2 text-center text-sm font-semibold rounded-full transition-all ${view === 'login' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                  {t('auth.login')}
                </button>
                <button onClick={() => { setView('signup'); setError(''); }} data-testid="auth-signup-tab"
                  className={`flex-1 py-2 text-center text-sm font-semibold rounded-full transition-all ${view === 'signup' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                  {t('auth.signup')}
                </button>
              </div>

              {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl" data-testid="auth-error">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                {view === 'signup' && (
                  <input type="text" placeholder={t('auth.name')} value={name} onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="auth-name-input"
                    className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors placeholder:text-slate-400" />
                )}
                <input type="email" placeholder={t('auth.email')} value={email} onChange={(e) => setEmail(e.target.value)} required
                  data-testid="auth-email-input"
                  className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors placeholder:text-slate-400" />
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} placeholder={t('auth.password')} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                    data-testid="auth-password-input"
                    className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 pr-12 text-slate-900 transition-colors placeholder:text-slate-400" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1">
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                {view === 'login' && (
                  <div className="text-right">
                    <button type="button" onClick={() => { setView('forgot'); setError(''); setResetEmail(email); }}
                      data-testid="forgot-password-link"
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">
                      {t('auth.forgot')}
                    </button>
                  </div>
                )}

                <button type="submit" disabled={loading} data-testid="auth-submit-button"
                  className="w-full bg-slate-900 text-white py-3 rounded-full font-semibold active:scale-[0.98] transition-transform disabled:opacity-50">
                  {loading ? t('auth.please_wait') : (view === 'login' ? t('auth.submit_login') : t('auth.submit_signup'))}
                </button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-500">{t('auth.or_continue')}</span></div>
              </div>

              <button data-testid="auth-google-button" onClick={() => handleSocialLogin('Google')}
                className="w-full bg-white border border-slate-200 py-3 rounded-full font-semibold flex items-center justify-center gap-3 active:scale-[0.98] transition-all hover:bg-slate-50">
                <FcGoogle size={24} /><span className="text-slate-700">{t('auth.google')}</span>
              </button>
            </motion.div>
          )}

          {/* ─── Forgot Password: Enter Email ─── */}
          {view === 'forgot' && (
            <motion.div key="forgot" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <button onClick={goBackToLogin} data-testid="back-to-login"
                className="flex items-center gap-2 text-slate-600 text-sm font-medium mb-6 hover:text-slate-900 transition-colors">
                <ArrowLeft size={18} /> {t('auth.back_login')}
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-blue-600" size={32} />
                </div>
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('auth.forgot_title')}</h2>
                <p className="text-sm text-slate-600 mt-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {t('auth.forgot_desc')}
                </p>
              </div>

              {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl mb-4" data-testid="forgot-error">{error}</div>}

              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <input type="email" placeholder={t('auth.email')} value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required
                  data-testid="forgot-email-input"
                  className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors placeholder:text-slate-400" />
                <button type="submit" disabled={loading || cooldown > 0} data-testid="forgot-submit-button"
                  className="w-full bg-slate-900 text-white py-3 rounded-full font-semibold active:scale-[0.98] transition-transform disabled:opacity-50">
                  {loading ? t('auth.sending') : cooldown > 0 ? `${t('auth.send_code')} (${cooldown}s)` : t('auth.send_code')}
                </button>
              </form>
            </motion.div>
          )}

          {/* ─── Reset Password: Enter Code + New Password ─── */}
          {view === 'code' && (
            <motion.div key="code" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <button onClick={() => { setView('forgot'); setError(''); setSuccess(''); }} data-testid="back-to-forgot"
                className="flex items-center gap-2 text-slate-600 text-sm font-medium mb-6 hover:text-slate-900 transition-colors">
                <ArrowLeft size={18} /> {t('common.back')}
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="text-emerald-600" size={32} />
                </div>
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('auth.enter_code')}</h2>
                <p className="text-sm text-slate-600 mt-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {t('auth.code_desc')}
                </p>
              </div>

              {success && <div className="bg-emerald-50 text-emerald-700 text-sm p-3 rounded-xl mb-4" data-testid="reset-success-msg">{success}</div>}
              {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl mb-4" data-testid="reset-error">{error}</div>}

              <form onSubmit={handleResetSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('auth.reset_code')}</label>
                  <input type="text" placeholder={t('auth.code_placeholder')} value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required maxLength={6} inputMode="numeric" pattern="[0-9]{6}"
                    data-testid="reset-code-input"
                    className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors placeholder:text-slate-400 text-center text-2xl font-mono tracking-[0.5em]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('auth.new_password')}</label>
                  <div className="relative">
                    <input type={showNewPassword ? 'text' : 'password'} placeholder="Min 8 characters" value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} required minLength={8}
                      data-testid="new-password-input"
                      className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 pr-12 text-slate-900 transition-colors placeholder:text-slate-400" />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} data-testid="toggle-new-password-visibility"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1">
                      {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('auth.confirm_password')}</label>
                  <div className="relative">
                    <input type={showConfirmPassword ? 'text' : 'password'} placeholder="Re-enter password" value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8}
                      data-testid="confirm-password-input"
                      className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 pr-12 text-slate-900 transition-colors placeholder:text-slate-400" />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-testid="toggle-confirm-password-visibility"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1">
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading || resetCode.length !== 6} data-testid="reset-submit-button"
                  className="w-full bg-slate-900 text-white py-3 rounded-full font-semibold active:scale-[0.98] transition-transform disabled:opacity-50">
                  {loading ? t('auth.resetting') : t('auth.reset_button')}
                </button>
              </form>
            </motion.div>
          )}

          {/* ─── Success ─── */}
          {view === 'reset-success' && (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldCheck className="text-emerald-600" size={40} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('auth.reset_success')}</h2>
              <p className="text-sm text-slate-600 mb-8" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {t('auth.reset_success_desc')}
              </p>
              <button onClick={goBackToLogin} data-testid="back-to-login-success"
                className="w-full bg-slate-900 text-white py-3 rounded-full font-semibold active:scale-[0.98] transition-transform">
                {t('auth.back_login')}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default AuthPage;
